import { HexColor } from "@shared/types/hexColor"
import { ThemeColorKey } from "@shared/zod/schemas/careGroupTheme"

export const DARK = "#222222"
export const WHITE = "#FEFEFE"
export const GRAY = "#374151"

/**
 * Accent palette tokens mirrored from `tailwind.config.mjs` (`accent.*`).
 * Charts/SVG render with raw hex (Tailwind classes don't apply there), so these
 * are the TS-side source of truth for accent hex values. Extend as charts need
 * more accent tokens.
 */
export const ACCENT = {
  green: "#DCF7E0",
  red: "#FFD5D5",
} as const

/**
 * Colors of the four bands of the phone volume chart (IT-4981), mirrored from
 * `tailwind.config.mjs` the same way `ACCENT` is. Listed baseline up. Every
 * adjacent pair is more than 100° apart in hue (220° → 25° → 132° → 288°) and
 * the tightest pair on lightness — orange over green — still differs by 20
 * points, so each edge holds even where a band is only a few pixels tall.
 * Green stays on "answered" and no red appears, so the product's green/red
 * open-vs-closed vocabulary is untouched.
 */
export const CALL_VOLUME = {
  /** blue-500 */
  medication: "#3E79F7",
  /** orange-500 */
  callbacks: "#FF7918",
  /** green-600 */
  answered: "#149F2A",
  /** purple-400 */
  unanswered: "#DD97F3",
} as const

/**
 * yellow-300: the consultation segment of the phone weekday chart. Sits directly
 * under the callback segment (`CALL_VOLUME.callbacks`, orange-500) and separates
 * from it on lightness (77% vs 55%).
 */
export const CALL_TYPE_CONSULTATION = "#FDDD8D"

export type OpenClosedState = "open" | "closed"

/**
 * A domain color as a raw `hex` — for SVG and inline styles, which Tailwind
 * classes can't reach — plus a matching Tailwind `bgClass` for everything else.
 */
export interface DomainColor {
  readonly hex: string
  readonly bgClass: string
}

/**
 * Open/closed status palette shared across care-group widgets so they read
 * consistently. Green = open, red = closed.
 */
export const OPEN_CLOSED_COLORS: Record<OpenClosedState, DomainColor> = {
  open: { hex: ACCENT.green, bgClass: "bg-accent-green" },
  closed: { hex: ACCENT.red, bgClass: "bg-accent-red" },
}

/** Stroke color of the selected-municipality outline (neutral gray-700). */
export const MUNICIPALITY_OUTLINE_COLOR = GRAY

/**
 * The effective colors when a care-group theme color is unset. Mirrors the
 * `:root` defaults of the `--theme-*` variables in index.css, the same way
 * `ACCENT` mirrors `tailwind.config.mjs`.
 */
export const THEME_COLOR_DEFAULTS: Record<ThemeColorKey, string> = {
  accentColor: "#1A3353",
  backgroundColor: "#FFFFFF",
  buttonBackgroundColor: "#3E79F7",
  buttonTextColor: "#FFFFFF",
}

/** Color picker quick access */
export const SAVED_COLORS = [
  "#EF4444", // Red
  "#F97316", // Orange
  "#FACC15", // Yellow
  "#4ADE80", // Lime green
  "#2DD4BF", // Turquoise
  "#3B82F6", // Royal blue
  "#6467F2", // Purple
  "#EC4899", // Hot pink
  "#F43F5E", // Coral pink
  "#D946EF", // Bright purple
  "#8B5CF6", // Lavender
  "#0EA5E9", // Sky blue
  "#10B981", // Emerald green
  "#84CC16", // Yellow-green
]

/** Subtle (light) colors */
export const LIGHT_COLORS = [
  "#E6E9F4", // Pale lavender
  "#DBECFC", // Light sky blue
  "#DBF8FC", // Pale cyan
  "#DCF7E0", // Mint green
  "#EDFBCE", // Pale lime
  "#FFF6C9", // Light yellow
  "#FCE7DB", // Peach
  "#FFD5D5", // Light pink
  "#FFE0F4", // Pale rose
  "#F3E4FF", // Light lilac
]

const RANDOM_COLORS = [DARK].concat(SAVED_COLORS)

/** Get a (random) color. Provide an `offset` to cycle through the colors (optional) */
export function getRandomColor(): string
export function getRandomColor(offset: number): string
export function getRandomColor(offset?: number): string {
  if (offset === undefined) {
    return RANDOM_COLORS[Math.floor(Math.random() * RANDOM_COLORS.length)]
  }
  return RANDOM_COLORS[offset % RANDOM_COLORS.length]
}

/**
 * Deterministically picks a visually distinct color for a given index, unbounded
 * by any fixed palette. Hues are spaced by the golden angle (~137.5°) so even
 * large sets (e.g. 50-100 items on a map) stay well separated and the same index
 * always yields the same color. Use when you need stable per-item colors for an
 * arbitrary count rather than cycling a short list.
 */
export function getDistinctColor(index: number): string {
  const hue = (index * 137.508) % 360
  return `hsl(${hue}deg 65% 55%)`
}

/** Fallback distinct color for callers that may not have a resolved index. */
export const DEFAULT_DISTINCT_COLOR = getDistinctColor(0)

/** Maps an assignment index to a color — raw hex/hsl for SVG, or a Tailwind class. */
export type Palette = (index: number) => string

/**
 * Assigns each not-yet-assigned key the next color in the palette, filling
 * `colors` in place, and returns the map. Keys already in the map keep their
 * color, so it works both upfront (fresh map + the full key set) and
 * incrementally (repeated calls as new keys appear).
 */
export const assignColors = (
  colors: Map<string, string>,
  keys: Iterable<string>,
  palette: Palette = getDistinctColor
): Map<string, string> => {
  for (const key of keys) {
    if (!colors.has(key)) {
      colors.set(key, palette(colors.size))
    }
  }
  return colors
}

/**
 * Determine "brightness" of a color. Brightness of `0` is black and `255` is
 * white. Returns `NaN` for values that are not a hex color, so comparisons
 * against thresholds are false and callers fall back to their defaults.
 */
export function getBrightness(hexColor: string): number {
  return HexColor.parse(hexColor)?.brightness() ?? NaN
}

// Determine `text` color to use on a `hexColor` background
export function getContrastColor(hexBackground: string): string {
  const brightness = getBrightness(hexBackground)

  // Return white text color for dark backgrounds and vice-versa
  return brightness < 148 ? WHITE : DARK
}

// Determine if chosen `hexColor` is hard to read on a `white` background
export function isTooBrightOnWhiteBackground(hexColor: string): boolean {
  return getBrightness(hexColor) > 210
}
